import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, User, Upload, Check, AlertCircle, X } from "lucide-react";
import apiService from "../../services/api";

const TeamMemberSignup = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [invitation, setInvitation] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    password: "",
    confirmPassword: "",
    profileImage: null,
  });

  const [profilePreview, setProfilePreview] = useState(null);

  useEffect(() => {
    validateInvitation();
  }, [token]);

  const validateInvitation = async () => {
    try {
      const response = await apiService.validateInvitation(token);
      if (response.success) {
        setInvitation(response.invitation);
      } else {
        setError(response.message || "Invalid invitation");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Invalid or expired invitation");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("Image size must be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePreview(reader.result);
        setFormData({ ...formData, profileImage: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate passwords
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setSubmitting(true);

    try {
      const formDataForUpload = new FormData();
      formDataForUpload.append("token", token);
      formDataForUpload.append("name", formData.name);
      formDataForUpload.append("password", formData.password);

      // Append the file if it exists
      if (formData.profileImage) {
        // Convert base64 to Blob if needed
        const blob = await fetch(formData.profileImage).then((r) => r.blob());
        formDataForUpload.append("profileImage", blob, "profile.jpg");
      }

      const response = await apiService.acceptInvitation(formDataForUpload);

      if (response.success) {
        navigate("/login", {
          state: { message: "Account created successfully! Please log in." },
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create account");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0C0E12] flex items-center justify-center">
        <Loader2 className="animate-spin h-8 w-8 text-purple-500" />
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-[#0C0E12] flex items-center justify-center">
        <div className="max-w-md w-full text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">
            Invalid Invitation
          </h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate("/login")}
            className="text-purple-500 hover:text-purple-400"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0E12] flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        {/* Company Logo and Info */}
        <div className="text-center mb-8">
          {invitation?.company?.logoUrl && (
            <img
              src={invitation.company.logoUrl}
              alt={invitation.company.name}
              className="h-16 w-auto mx-auto mb-4"
            />
          )}
          <h1 className="text-3xl font-bold text-white mb-2">
            Join {invitation?.company?.name}
          </h1>
          <p className="text-gray-400">
            You've been invited to join the team. Create your account below.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Profile Picture (Optional)
            </label>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-gray-800 flex items-center justify-center overflow-hidden">
                  {profilePreview ? (
                    <img
                      src={profilePreview}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User size={32} className="text-gray-500" />
                  )}
                </div>
                {profilePreview && (
                  <button
                    type="button"
                    onClick={() => {
                      setProfilePreview(null);
                      setFormData({ ...formData, profileImage: null });
                    }}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              <label
                htmlFor="profile-upload"
                className="cursor-pointer text-sm text-purple-500 hover:text-purple-400"
              >
                <input
                  id="profile-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  disabled={submitting}
                />
                {profilePreview ? "Change photo" : "Upload photo"}
              </label>
            </div>
          </div>

          {/* Email (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email
            </label>
            <input
              type="email"
              value={invitation?.email || ""}
              disabled
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-400"
            />
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Full Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
              placeholder="John Doe"
              required
              disabled={submitting}
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Password
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
              placeholder="••••••••"
              minLength={8}
              required
              disabled={submitting}
            />
            <p className="text-xs text-gray-500 mt-1">
              Must be at least 8 characters
            </p>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Confirm Password
            </label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) =>
                setFormData({ ...formData, confirmPassword: e.target.value })
              }
              className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
              placeholder="••••••••"
              required
              disabled={submitting}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 text-white rounded-lg font-medium transition-all duration-300 relative 
               hover:scale-[1.02] hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background:
                "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
              border: "1px solid",
              borderImageSource:
                "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
              boxShadow: `
                0px 36px 32px -16px #7F56D936,
                0px 8px 16px 0px #7F56D91F,
                0px 2px 4px -8px #7F56D914,
                0px 8px 24px -16px #FFFFFF3D inset,
                0px -24px 32px 0px #FFFFFF38 inset,
                0px 0px 12px 0px #FFFFFF inset
              `,
              borderImageSlice: "1",
              transition: "all 0.3s ease",
            }}
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="animate-spin h-4 w-4" />
                Creating account...
              </span>
            ) : (
              "Create Account"
            )}
          </button>
        </form>

        <p className="text-center text-sm text-gray-400 mt-6">
          Already have an account?{" "}
          <button
            onClick={() => navigate("/login")}
            className="text-purple-500 hover:text-purple-400"
          >
            Log in
          </button>
        </p>
      </div>
    </div>
  );
};

export default TeamMemberSignup;
